import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Gem, HelpCircle, History, CircleDot } from 'lucide-react'
import { toast } from 'react-hot-toast'
import useDoubleStore from '../store/useDoubleStore'
import useAppStore from '../../../store/useAppStore'
import useRealtimeGame from '../../../hooks/useRealtimeGame'
import DoubleWheel from '../components/DoubleWheel'
import DoubleBetsFeed from '../components/DoubleBetsFeed'
import GamePerformanceChart from '../../../components/GamePerformanceChart'
import HelpModal from '../../../components/HelpModal'
import MobileBetSheet from '../../../components/MobileBetSheet'
import useAudioManager from '../../../hooks/useAudioManager'
import { hapticImpact, hapticNotify } from '../../../utils/haptic'
import { sendYandexGoal } from '../../../services/yandexMetrikaService'
import TeamCapturePreview from '../../team/components/TeamCapturePreview'

const getMyBetKey = (bet, index) => {
  const stableId = bet?.id ?? bet?.bet_id ?? bet?.created_at ?? null

  if (stableId !== null && stableId !== undefined && stableId !== '') {
    return `my-bet-${stableId}`
  }

  const color = String(bet?.color || 'unknown')
  const sum = Number(bet?.sum || 0)
  const crystal = Number(bet?.crystal || 0)
  const itemsCount = Array.isArray(bet?.bet_items) ? bet.bet_items.length : 0
  const chatId = String(bet?.chat_id || 'anon')

  return `my-bet-fallback-${chatId}-${color}-${sum}-${crystal}-${itemsCount}-${index}`
}

const getStatusPollDelay = (status) => {
  if (status === 'raffle') return 300
  if (status === 'finished') return 300
  if (status === 'result') return 300
  return 1000
}

const getRecentRoundCircleClass = (color) => {
  const normalizedColor = String(color || '').toLowerCase()

  if (normalizedColor === 'red') {
    return 'bg-red-500 border-red-300/70 text-red-50'
  }

  if (normalizedColor === 'black') {
    return 'bg-slate-900 border-slate-300/60 text-slate-100'
  }

  return 'bg-emerald-500 border-emerald-300/70 text-emerald-50'
}

const Double = () => {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const isGuest = useAppStore((state) => state.isGuest)
  const openLogin = useAppStore((state) => state.openLogin)
  const { playSound } = useAudioManager({
    hover: 'hover',
    betAccepted: 'doubleBetAccepted',
    win: 'doubleWin',
    select: 'select'
  })
  const {
    activeRound,
    engine,
    isConnected,
    isModeDisabled,
    limits,
    inventory,
    balance,
    isBetting,
    ownBetAcks,
    loadActiveRound,
    loadBetForm,
    loadHistoryFast,
    placeBet
  } = useDoubleStore()

  const [isBetModalOpen, setIsBetModalOpen] = useState(false)
  const [isHelpOpen, setIsHelpOpen] = useState(false)
  const [selectedColor, setSelectedColor] = useState(null)
  const [countdownMs, setCountdownMs] = useState(Math.max(0, Number(activeRound?.second_to_raffle || 0) * 1000))
  const [recentRounds, setRecentRounds] = useState([])
  const [myBets, setMyBets] = useState([])
  const realtime = useRealtimeGame({
    game: 'double', store: useDoubleStore, side: 'color',
    onReconnect: () => refreshRecentRounds(),
    onInventory: () => setIsBetDataReady(true),
    onResult: (data) => setRecentRounds((rounds) => [
      { id: String(data.round_id), winColor: data.win_color, winSector: String(data.win_sector) },
      ...rounds.filter((round) => round.id !== String(data.round_id))
    ].slice(0, 10)),
    onMyResult: (data) => setMyBets((betsData) => [data, ...betsData.filter((bet) => String(bet.round_id ?? bet.id) !== String(data.round_id))].slice(0, 50))
  })

  const pollTimeoutRef = useRef(null)
  const timerIntervalRef = useRef(null)
  const abortControllerRef = useRef(null)
  const isPollingActiveRef = useRef(false)
  const countdownTargetTsRef = useRef(null)
  const lastCountdownSyncRef = useRef({ roundId: null, status: null })
  const winToastRoundIdRef = useRef(null)
  const betDataRefreshPromiseRef = useRef(null)
  const betDataInitializedRef = useRef(false)
  const betDataIntervalRef = useRef(null)
  const recentHistoryPromiseRef = useRef(null)
  const recentHistoryRefreshTimeoutRef = useRef(null)
  const lastRecentHistoryRoundIdRef = useRef('initial')
  const shouldPlayBetAcceptedSoundRef = useRef(false)
  const prevMyBetKeysRef = useRef(new Set())
  const [isBetDataReady, setIsBetDataReady] = useState(false)

  const roundId = activeRound?.id
  const isRoundReady = Boolean(roundId)
  const roundStatus = activeRound?.status || null
  const bets = useMemo(() => (Array.isArray(activeRound?.bets) ? activeRound.bets : []), [activeRound?.bets])
  const roundAcks = useMemo(() => engine === 'node'
    ? ownBetAcks.filter((bet) => String(bet.round_id) === String(roundId))
    : [], [engine, ownBetAcks, roundId])
  const myColorSums = useMemo(() => {
    const sums = { red: 0, black: 0, green: 0 }
    const confirmed = { ...sums }
    for (const bet of bets) {
      if (bet?.your_bet && ['red', 'black', 'green'].includes(bet.color)) sums[bet.color] += Number(bet.sum || 0)
    }
    for (const bet of roundAcks) {
      if (['red', 'black', 'green'].includes(bet.color)) confirmed[bet.color] += Number(bet.sum || 0)
    }
    return Object.fromEntries(Object.keys(sums).map((color) => [color, Math.max(sums[color], confirmed[color])]))
  }, [bets, roundAcks])
  const stats100 = useMemo(() => activeRound?.stats_100 || {}, [activeRound?.stats_100])
  const modalInventory = useMemo(() => (Array.isArray(inventory) ? inventory : []), [inventory])

  const maxBetsReached = engine === 'node' && limits?.max_bets != null && Number.isFinite(Number(limits.max_bets))
    && Math.max(bets.filter((bet) => bet?.your_bet).length, roundAcks.length) >= Number(limits.max_bets)
  const isResultPhase = ['result', 'finished'].includes(roundStatus)
  const winColor = activeRound?.win_color || null
  const currentBalance = Number(balance || 0)
  const minBet = Number(limits?.min_bet ?? activeRound?.min_bet ?? 100)
  const maxBet = Math.max(0, Number(limits?.max_bet ?? activeRound?.max_bet ?? 2500000)
    - (engine === 'node' ? Object.values(myColorSums).reduce((sum, amount) => sum + amount, 0) : 0))
  const limitReached = engine === 'node' && maxBet < minBet
  const isBetLocked = !isRoundReady || isModeDisabled || maxBetsReached || limitReached || ['raffle', 'result', 'finished'].includes(roundStatus)
    || (engine === 'node' && (!isConnected || !activeRound?.ends_at || Date.now() + realtime.clockOffset >= activeRound.ends_at))

  const handleStopPolling = () => {
    isPollingActiveRef.current = false

    if (pollTimeoutRef.current) {
      clearTimeout(pollTimeoutRef.current)
      pollTimeoutRef.current = null
    }

    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
      abortControllerRef.current = null
    }
  }

  const startPolling = useCallback(async () => {
    if (!isPollingActiveRef.current) return

    const controller = new AbortController()
    abortControllerRef.current = controller

    try {
      const round = await loadActiveRound(controller.signal)
      if (!isPollingActiveRef.current) return

      const nextDelay = isGuest ? 10000 : getStatusPollDelay(round?.status)
      pollTimeoutRef.current = setTimeout(startPolling, nextDelay)
    } catch (error) {
      const isAbortError = error?.name === 'AbortError' || controller.signal.aborted
      if (!isPollingActiveRef.current || isAbortError) return

      pollTimeoutRef.current = setTimeout(startPolling, isGuest ? 10000 : 1500)
    } finally {
      if (abortControllerRef.current === controller) {
        abortControllerRef.current = null
      }
    }
  }, [isGuest, loadActiveRound])

  useEffect(() => {
    if (engine !== 'worker') return undefined
    isPollingActiveRef.current = true
    startPolling()
    return () => handleStopPolling()
  }, [engine, startPolling])

  useEffect(() => {
    const nextRoundId = activeRound?.id || null
    const nextStatus = activeRound?.status || null
    const nextMilliseconds = Math.max(0, Number(activeRound?.second_to_raffle || 0) * 1000)
    const prevSync = lastCountdownSyncRef.current

    const isNewRound = prevSync.roundId !== nextRoundId
    const enteredBetStatus = !['new', 'started'].includes(prevSync.status) && ['new', 'started'].includes(nextStatus)

    if ((isNewRound || enteredBetStatus || (engine === 'node' && activeRound?.ends_at)) && ['new', 'started'].includes(nextStatus)) {
      countdownTargetTsRef.current = engine === 'node' && activeRound?.ends_at
        ? activeRound.ends_at - realtime.clockOffset
        : Date.now() + nextMilliseconds
      setCountdownMs(Math.max(0, countdownTargetTsRef.current - Date.now()))
    }

    if (!['new', 'started'].includes(nextStatus)) {
      countdownTargetTsRef.current = null
    }

    lastCountdownSyncRef.current = {
      roundId: nextRoundId,
      status: nextStatus
    }
  }, [activeRound?.id, activeRound?.status, activeRound?.second_to_raffle, activeRound?.ends_at, engine, realtime.clockOffset])

  useEffect(() => {
    if (timerIntervalRef.current) {
      clearInterval(timerIntervalRef.current)
      timerIntervalRef.current = null
    }

    if (!['new', 'started'].includes(roundStatus)) return

    timerIntervalRef.current = setInterval(() => {
      if (!countdownTargetTsRef.current) return

      const remainingMs = Math.max(0, countdownTargetTsRef.current - Date.now())
      setCountdownMs(remainingMs)

      if (remainingMs <= 0 && timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current)
        timerIntervalRef.current = null
      }
    }, 33)

    return () => {
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current)
        timerIntervalRef.current = null
      }
    }
  }, [roundStatus])

  const refreshBetData = useCallback(async ({ showError = false } = {}) => {
    if (isGuest) return false
    if (betDataRefreshPromiseRef.current) {
      return betDataRefreshPromiseRef.current
    }

    betDataRefreshPromiseRef.current = (async () => {
      try {
        await loadBetForm()
        setIsBetDataReady(true)

        return true
      } catch (error) {
        if (showError) {
          hapticNotify('error')
          toast.error(t('double.toasts.betFormError', 'Не удалось загрузить форму ставки'))
        }
        return false
      } finally {
        betDataRefreshPromiseRef.current = null
      }
    })()

    return betDataRefreshPromiseRef.current
  }, [isGuest, loadBetForm, t])

  const refreshRecentRounds = useCallback(async () => {
    if (recentHistoryPromiseRef.current) {
      return recentHistoryPromiseRef.current
    }

    recentHistoryPromiseRef.current = (async () => {
      try {
        const { rounds, myBets: betsData } = await loadHistoryFast()

        setMyBets((current) => useDoubleStore.getState().engine === 'node'
          ? [...current, ...betsData.filter((bet) => !current.some((item) => String(item.round_id ?? item.id) === String(bet.round_id ?? bet.id)))].slice(0, 50)
          : betsData)

        const normalizedRounds = rounds
          .filter((round) => round?.win_sector !== null && typeof round?.win_sector !== 'undefined')
          .slice(0, 10)
          .map((round) => ({
            id: String(round?.id || ''),
            winColor: String(round?.win_color || '').toLowerCase(),
            winSector: String(round?.win_sector ?? '—')
          }))

        setRecentRounds((current) => useDoubleStore.getState().engine === 'node'
          ? [...current, ...normalizedRounds.filter((round) => !current.some((item) => item.id === round.id))].slice(0, 10)
          : normalizedRounds)
      } catch (error) {
        // Ошибку истории не показываем как toast, чтобы не мешать игровому процессу
      } finally {
        recentHistoryPromiseRef.current = null
      }
    })()

    return recentHistoryPromiseRef.current
  }, [loadHistoryFast])

  useEffect(() => {
    if (!roundId || !isResultPhase || (engine === 'node' && !Array.isArray(activeRound?.winners))) return
    if (winToastRoundIdRef.current === roundId) return

    const hasMyWinningBet = bets.some((bet) => bet?.your_bet && bet?.win === true) || myColorSums[winColor] > 0
    if (!hasMyWinningBet) return

    winToastRoundIdRef.current = roundId
    playSound('win')
    // Тактильная обратная связь на победу: тяжёлый impact + success-нотификация
    hapticImpact('heavy')
    hapticNotify('success')
    toast.success(t('double.toasts.youWon', 'Вы выиграли!'))
  }, [activeRound?.winners, bets, engine, isResultPhase, myColorSums, playSound, roundId, t, winColor])

  useEffect(() => {
    const myBetKeys = new Set(
      bets
        .map((bet, index) => ({ bet, index }))
        .filter(({ bet }) => Boolean(bet?.your_bet))
        .map(({ bet, index }) => getMyBetKey(bet, index))
    )

    const prevMyBetKeys = prevMyBetKeysRef.current
    const hasNewMyBet = Array.from(myBetKeys).some((key) => !prevMyBetKeys.has(key))

    if (shouldPlayBetAcceptedSoundRef.current && hasNewMyBet) {
      playSound('betAccepted')
      shouldPlayBetAcceptedSoundRef.current = false
    }

    prevMyBetKeysRef.current = myBetKeys
  }, [bets, playSound])

  useEffect(() => {
    if (!engine || isGuest) return undefined
    if (!betDataInitializedRef.current) {
      betDataInitializedRef.current = true
      refreshBetData()
    }
    if (engine === 'node') return undefined

    if (betDataIntervalRef.current) {
      clearInterval(betDataIntervalRef.current)
      betDataIntervalRef.current = null
    }

    betDataIntervalRef.current = setInterval(() => {
      refreshBetData()
    }, 5000)

    return () => {
      if (betDataIntervalRef.current) {
        clearInterval(betDataIntervalRef.current)
        betDataIntervalRef.current = null
      }
    }
  }, [engine, isGuest, refreshBetData])

  useEffect(() => {
    if (recentHistoryRefreshTimeoutRef.current) {
      clearTimeout(recentHistoryRefreshTimeoutRef.current)
      recentHistoryRefreshTimeoutRef.current = null
    }

    const nextRoundId = roundId || null
    const prevRoundId = lastRecentHistoryRoundIdRef.current
    const isFirstHistorySync = prevRoundId === 'initial'
    const isNewRound = !isFirstHistorySync && prevRoundId !== nextRoundId

    lastRecentHistoryRoundIdRef.current = nextRoundId

    if (isNewRound && engine === 'node') return undefined

    if (isNewRound) {
      recentHistoryRefreshTimeoutRef.current = setTimeout(() => {
        refreshRecentRounds()
        recentHistoryRefreshTimeoutRef.current = null
      }, 2000)

      return () => {
        if (recentHistoryRefreshTimeoutRef.current) {
          clearTimeout(recentHistoryRefreshTimeoutRef.current)
          recentHistoryRefreshTimeoutRef.current = null
        }
      }
    }

    if (isFirstHistorySync) refreshRecentRounds()

    return () => {
      if (recentHistoryRefreshTimeoutRef.current) {
        clearTimeout(recentHistoryRefreshTimeoutRef.current)
        recentHistoryRefreshTimeoutRef.current = null
      }
    }
  }, [engine, refreshRecentRounds, roundId])

  const handleOpenBetModal = async (color) => {
    if (isGuest) return openLogin()
    if (isBetLocked) return

    playSound('select')
    // Импульс на клик по цветной кнопке ставки (важное действие)
    hapticImpact('medium')

    const prepared = isBetDataReady ? true : await refreshBetData({ showError: true })
    if (!prepared) return

    const currentRound = useDoubleStore.getState().activeRound
    if (currentRound?.id !== roundId || ['raffle', 'result', 'finished'].includes(currentRound?.status)) return

    setSelectedColor(color)
    setIsBetModalOpen(true)
  }

  const handleSubmitBet = async ({ color, crystal, items }) => {
    if (!roundId) {
      hapticNotify('error')
      toast.error(t('double.toasts.roundUnavailable', 'Раунд недоступен'))
      return false
    }

    try {
      if (engine === 'node') {
        await realtime.placeBet({ roundId, color, crystal, items })
        playSound('betAccepted')
      } else {
        await placeBet({ roundId, color, crystal, items })
        shouldPlayBetAcceptedSoundRef.current = true
        await Promise.allSettled([loadActiveRound(), refreshBetData()])
      }
      sendYandexGoal('double-bet')

      // Успешная отправка ставки
      hapticNotify('success')
      setIsBetModalOpen(false)
      return true
    } catch (error) {
      if (error?.status === 409 || error?.message === 'engine_switched') {
        await loadActiveRound().catch(() => {})
        if (useDoubleStore.getState().engine === 'node') realtime.requestState()
        setIsBetModalOpen(false)
        return false
      }
      if (engine === 'node') {
        hapticNotify('error')
        toast.error(t(`realtime.alerts.${error.message}`, t('double.toasts.betError', 'Не удалось отправить ставку')))
        return false
      }
      // Любая ошибка отправки ставки = error-нотификация
      hapticNotify('error')
      if (error.message === 'not enough balance') {
        toast.error(t('double.toasts.notEnoughBalance', 'Недостаточно кристаллов'))
        return false
      }
      if (error.message === 'error sum') {
        toast.error(t('double.toasts.minBet', 'Минимальная ставка 100 кристаллов'))
        return false
      }
      if (error.message === 'error max bet') {
        toast.error(t('double.toasts.maxBet', 'Максимальная сумма ставки 2 500 000'))
        return false
      }
      toast.error(t('double.toasts.betError', 'Не удалось отправить ставку'))
      return false
    }
  }

  const statsRows = useMemo(() => {
    const total = Number(stats100.red || 0) + Number(stats100.black || 0) + Number(stats100.green || 0)
    const safeTotal = total > 0 ? total : 1

    return [
      { key: 'red', label: t('double.colors.red', 'Красный'), value: Number(stats100.red || 0), colorClass: 'bg-red-500' },
      { key: 'black', label: t('double.colors.black', 'Чёрный'), value: Number(stats100.black || 0), colorClass: 'bg-slate-500' },
      { key: 'green', label: t('double.colors.green', 'Зелёный'), value: Number(stats100.green || 0), colorClass: 'bg-emerald-400' }
    ].map((item) => ({
      ...item,
      percent: (item.value / safeTotal) * 100
    }))
  }, [stats100, t])

  const statsTotalRounds = useMemo(() => {
    return statsRows.reduce((acc, row) => acc + Number(row.value || 0), 0)
  }, [statsRows])

  const redShare = statsRows.find((row) => row.key === 'red')?.percent || 0
  const blackShare = statsRows.find((row) => row.key === 'black')?.percent || 0
  const ringGradient = `conic-gradient(
    rgba(239,68,68,0.98) 0% ${redShare}%,
    rgba(100,116,139,0.98) ${redShare}% ${redShare + blackShare}%,
    rgba(16,185,129,0.98) ${redShare + blackShare}% 100%
  )`

  const helpSteps = useMemo(() => ([
    t('double.help.step1', 'Выберите цвет ставки: красный или чёрный дают x2, зелёный даёт x14.'),
    t('double.help.step2', 'Добавьте кристаллы и/или предметы, минимальная ставка — 100, а максимальная — 2 500 000.'),
    t('double.help.step3', 'Дождитесь розыгрыша: колесо определит сектор и выигравший цвет раунда.')
  ]), [t])

  const handleColorButtonHover = () => {
    playSound('hover')
  }

  if (isModeDisabled) {
    return <div className="mx-auto max-w-4xl rounded-2xl border border-white/10 bg-surface/50 p-6 text-center text-text-secondary">
      {t('realtime.disabled')}
    </div>
  }

  return (
    <div className="px-4 py-6 pb-16 lg:px-6 lg:py-2 lg:pb-8 overflow-x-clip">
      <div className="mx-auto max-w-7xl space-y-6 lg:space-y-8">
        <div className="flex items-center justify-center">
          <div className="flex items-center gap-2">
            <h1 className="flex items-center gap-2 text-2xl lg:text-4xl font-bold text-accent-primary uppercase italic">
              <CircleDot className="w-6 h-6 shrink-0 lg:w-8 lg:h-8" />
              <span>{t('double.title', 'Double')}</span>
            </h1>
            <button
              type="button"
              onClick={() => setIsHelpOpen(true)}
              className="p-1.5 rounded-full bg-dark-secondary/50 hover:bg-dark-secondary transition-colors"
              aria-label={t('double.help.button', 'Справка по режиму Double')}
            >
              <HelpCircle className="w-5 h-5 text-text-secondary" />
            </button>
          </div>
        </div>
        {engine === 'node' && !isConnected && realtime.showReconnectNotice && (
          <div className="flex justify-center">
            <span role="status" className="rounded-full border border-white/10 bg-surface px-3 py-1 text-xs text-text-secondary">
              {t('realtime.reconnecting')}
            </span>
          </div>
        )}
        <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_350px]">
          <div className="space-y-6">
            <DoubleWheel
              roundId={roundId}
              status={roundStatus}
              prevSector={activeRound?.prev_round?.win_sector}
              winSector={activeRound?.win_sector}
              winColor={activeRound?.win_color}
              countdownMs={countdownMs}
              raffleSeconds={activeRound?.second_to_finish_raffle}
              raffleDurationSeconds={activeRound?.raffle_time}
              raffleEndTimeMs={engine === 'node' && activeRound?.anim_ends_at
                ? Number(activeRound.anim_ends_at) - realtime.clockOffset
                : activeRound?.received_at && activeRound?.second_to_finish_raffle != null
                  ? activeRound.received_at + Number(activeRound.second_to_finish_raffle) * 1000
                  : null}
            />

            <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
              <button
                disabled={isBetLocked}
                onClick={() => handleOpenBetModal('red')}
                onMouseEnter={handleColorButtonHover}
                className={`relative min-h-[72px] overflow-hidden rounded-2xl border px-4 py-3 text-left transition-all duration-300 ${
                  isResultPhase && winColor === 'red'
                    ? 'border-red-300/80 bg-gradient-to-br from-red-500/70 via-rose-500/50 to-red-900/60 shadow-[0_0_30px_rgba(239,68,68,0.5)] animate-pulse ring-2 ring-red-400/60'
                    : 'border-red-300/45 bg-gradient-to-br from-red-500/50 via-rose-500/30 to-red-900/45 shadow-[0_8px_24px_rgba(239,68,68,0.28)] hover:-translate-y-0.5 hover:border-red-200/70 hover:shadow-[0_14px_34px_rgba(239,68,68,0.45)] active:translate-y-0'
                } ${isBetLocked && !(isResultPhase && winColor === 'red') ? 'opacity-45 transform-none !shadow-none' : ''}`}
              >
                <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_20%_15%,rgba(255,255,255,0.24),transparent_45%)]" />
                <div className="relative flex flex-wrap items-center justify-between gap-x-2 gap-y-2">
                  <span className="text-2xl font-black not-italic leading-none tracking-tight text-red-50 sm:text-3xl"><span className="sr-only">{t('double.colors.red', 'Красное')} </span>×2</span>
                  {myColorSums.red > 0 && (
                    <span className="inline-flex items-center gap-1 text-sm font-medium tabular-nums text-red-100">
                      <span className="sr-only">{t('double.myBet', 'Моя ставка')}: </span>
                      {myColorSums.red.toLocaleString()} <Gem className="h-3.5 w-3.5 shrink-0" />
                    </span>
                  )}
                </div>
              </button>

              <button
                disabled={isBetLocked}
                onClick={() => handleOpenBetModal('black')}
                onMouseEnter={handleColorButtonHover}
                className={`relative min-h-[72px] overflow-hidden rounded-2xl border px-4 py-3 text-left transition-all duration-300 ${
                  isResultPhase && winColor === 'black'
                    ? 'border-slate-200/70 bg-gradient-to-br from-slate-600/90 via-slate-800/95 to-black/95 shadow-[0_0_30px_rgba(148,163,184,0.35)] animate-pulse ring-2 ring-slate-300/50'
                    : 'border-slate-300/35 bg-gradient-to-br from-slate-700/70 via-slate-900/80 to-black/90 shadow-[0_8px_24px_rgba(2,6,23,0.55)] hover:-translate-y-0.5 hover:border-slate-200/70 hover:shadow-[0_14px_34px_rgba(2,6,23,0.75)] active:translate-y-0'
                } ${isBetLocked && !(isResultPhase && winColor === 'black') ? 'opacity-45 transform-none !shadow-none' : ''}`}
              >
                <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_20%_15%,rgba(255,255,255,0.14),transparent_45%)]" />
                <div className="relative flex flex-wrap items-center justify-between gap-x-2 gap-y-2">
                  <span className="text-2xl font-black not-italic leading-none tracking-tight text-slate-100 sm:text-3xl"><span className="sr-only">{t('double.colors.black', 'Чёрное')} </span>×2</span>
                  {myColorSums.black > 0 && (
                    <span className="inline-flex items-center gap-1 text-sm font-medium tabular-nums text-slate-200">
                      <span className="sr-only">{t('double.myBet', 'Моя ставка')}: </span>
                      {myColorSums.black.toLocaleString()} <Gem className="h-3.5 w-3.5 shrink-0" />
                    </span>
                  )}
                </div>
              </button>

              <button
                disabled={isBetLocked}
                onClick={() => handleOpenBetModal('green')}
                onMouseEnter={handleColorButtonHover}
                className={`relative col-span-2 min-h-[72px] overflow-hidden rounded-2xl border px-4 py-3 text-left transition-all duration-300 md:col-span-1 ${
                  isResultPhase && winColor === 'green'
                    ? 'border-emerald-300/80 bg-gradient-to-br from-emerald-400/75 via-emerald-500/55 to-emerald-900/65 shadow-[0_0_30px_rgba(16,185,129,0.5)] animate-pulse ring-2 ring-emerald-400/60'
                    : 'border-emerald-300/45 bg-gradient-to-br from-emerald-400/55 via-emerald-500/35 to-emerald-900/50 shadow-[0_8px_24px_rgba(16,185,129,0.28)] hover:-translate-y-0.5 hover:border-emerald-200/70 hover:shadow-[0_14px_34px_rgba(16,185,129,0.45)] active:translate-y-0'
                } ${isBetLocked && !(isResultPhase && winColor === 'green') ? 'opacity-45 transform-none !shadow-none' : ''}`}
              >
                <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_20%_15%,rgba(255,255,255,0.24),transparent_45%)]" />
                <div className="relative flex flex-wrap items-center justify-between gap-x-2 gap-y-2">
                  <span className="text-2xl font-black not-italic leading-none tracking-tight text-emerald-50 sm:text-3xl"><span className="sr-only">{t('double.colors.green', 'Зелёное')} </span>×14</span>
                  {myColorSums.green > 0 && (
                    <span className="inline-flex items-center gap-1 text-sm font-medium tabular-nums text-emerald-100">
                      <span className="sr-only">{t('double.myBet', 'Моя ставка')}: </span>
                      {myColorSums.green.toLocaleString()} <Gem className="h-3.5 w-3.5 shrink-0" />
                    </span>
                  )}
                </div>
              </button>
            </div>

            <DoubleBetsFeed bets={bets} roundStatus={engine === 'node' && activeRound?.winners === null ? 'raffle' : roundStatus} winColor={activeRound?.win_color} />
          </div>

          <div className="space-y-6 h-fit">
            <div className="hidden lg:block">
              {isGuest ? <button type="button" onClick={openLogin} className="gradient-border-cta w-full rounded-xl p-4 font-bold text-dark-primary">{t('guest.login')}</button> : <MobileBetSheet
                variant="inline"
                mode="double"
                targets={[
                  { value: 'red', label: '×2', ariaLabel: `${t('double.colors.red', 'Красный')} ×2`, className: 'border-red-400/40 bg-red-500/20 text-red-100' },
                  { value: 'black', label: '×2', ariaLabel: `${t('double.colors.black', 'Чёрный')} ×2`, className: 'border-slate-400/40 bg-slate-800 text-slate-100' },
                  { value: 'green', label: '×14', ariaLabel: `${t('double.colors.green', 'Зелёный')} ×14`, className: 'border-emerald-400/40 bg-emerald-500/20 text-emerald-100' }
                ]}
                minBet={minBet}
                maxBet={maxBet}
                maxItems={engine === 'node' ? Number(limits?.max_items ?? Infinity) : Infinity}
                isDisabled={isBetLocked}
                disabledReason={maxBetsReached ? t('realtime.alerts.too_many_bets') : limitReached ? t('realtime.alerts.error_max_bet') : undefined}
                isLoading={isBetting}
                isDataReady={isBetDataReady}
                balance={currentBalance}
                inventory={modalInventory}
                onSubmit={handleSubmitBet}
              />}
            </div>

            {!isGuest && myBets.length > 0 && <GamePerformanceChart myBets={myBets} mode="double" gameName="Double" />}

            <div className="rounded-2xl border border-white/10 bg-surface/50 p-4">
              <h3 className="text-white font-semibold mb-3 hidden">{t('double.stats100.title', '100 последних раундов')}</h3>
              <div className="flex items-center gap-4">
                <div className="relative h-24 w-24 shrink-0 rounded-full p-[7px] shadow-[0_0_24px_rgba(16,185,129,0.18)]" style={{ background: ringGradient }}>
                  <div className="h-full w-full rounded-full bg-dark-primary/95 border border-white/10 flex flex-col items-center justify-center">
                    <span className="text-base font-bold text-white leading-none">{statsTotalRounds}</span>
                    <span className="text-[9px] uppercase tracking-wide text-text-secondary mt-1">{t('double.stats100.rounds', 'раундов')}</span>
                  </div>
                </div>

                <div className="min-w-0 flex-1 space-y-2">
                  {statsRows.map((row) => (
                    <div key={`ring-legend-${row.key}`} className="flex items-center justify-between text-xs">
                      <span className="inline-flex items-center gap-1.5 text-text-secondary min-w-0">
                        <span
                          className={`h-2.5 w-2.5 rounded-full shrink-0 ring-1 ring-white/15 ${row.key === 'red' ? 'bg-red-500' : row.key === 'black' ? 'bg-slate-500' : 'bg-emerald-400'}`}
                        />
                        <span className="truncate">{row.label}</span>
                      </span>

                      <span className="text-white font-medium tabular-nums">
                        {row.percent.toFixed(0)}%
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="mt-4 border-t border-white/10 pt-3">
                {recentRounds.length > 0 ? (
                  <div className="flex flex-wrap items-center justify-center gap-1.5">
                    {recentRounds.slice(0, 6).map((round) => (
                      <button
                        type="button"
                        key={round.id || `${round.winColor}-${round.winSector}`}
                        onClick={() => {
                          if (!round.id) return
                          navigate(`/double/history/${round.id}`)
                        }}
                        disabled={!round.id}
                        className={`inline-flex h-8 w-8 items-center justify-center rounded-full border text-xs font-bold tabular-nums  hover:scale-100 disabled:cursor-default disabled:hover:scale-100 ${getRecentRoundCircleClass(round.winColor)}`}
                        aria-label={t('double.history.openRound', 'Открыть раунд {{roundId}}', { roundId: round.id || round.winSector })}
                        title={`${round.winColor} #${round.winSector}`}
                      >
                        {round.winSector}
                      </button>
                    ))}

                    <button
                      type="button"
                      onClick={() => navigate('/double/history')}
                      className="inline-flex h-8 min-w-8 items-center justify-center rounded-full border border-white/20 bg-dark-primary/60 px-1 text-xs font-bold text-text-secondary transition-colors hover:text-white"
                      aria-label={t('double.history.moreRounds', 'Перейти в историю раундов')}
                      title={t('double.history.moreRounds', 'Перейти в историю раундов')}
                    >
                      ...
                    </button>
                  </div>
                ) : (
                  <p className="text-xs text-text-secondary hidden">
                    {t('double.history.empty', 'История пока недоступна')}
                  </p>
                )}
              </div>
            </div>

            <button
                type="button"
                onClick={() => navigate('/double/history')}
                className="mt-4 inline-flex items-center justify-center gap-2 px-3 py-1.5 rounded-full bg-surface text-text-secondary hover:text-white border border-white/10 transition-colors"
              >
                <History className="w-4 h-4" />
                <span className="text-sm">{t('double.buttons.history', 'История')}</span>
            </button>

          </div>
        </div>
        <TeamCapturePreview mode="double" className="mx-auto max-w-3xl" />
      </div>

      <HelpModal
        isOpen={isHelpOpen}
        onClose={() => setIsHelpOpen(false)}
        title={t('double.help.title', 'Как работает Double')}
        steps={helpSteps}
        highlightLastStep={false}
      />

      <MobileBetSheet
        isOpen={isBetModalOpen}
        mode="double"
        target={selectedColor}
        targetLabel={selectedColor ? t(`double.colors.${selectedColor}`) : ''}
        minBet={minBet}
        maxBet={maxBet}
        maxItems={engine === 'node' ? Number(limits?.max_items ?? Infinity) : Infinity}
        isDisabled={isBetLocked}
        disabledReason={maxBetsReached ? t('realtime.alerts.too_many_bets') : limitReached ? t('realtime.alerts.error_max_bet') : undefined}
        isLoading={isBetting}
        balance={currentBalance}
        inventory={modalInventory}
        onClose={() => setIsBetModalOpen(false)}
        onSubmit={handleSubmitBet}
      />
    </div>
  )
}

export default Double
